import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { ApiRequestError } from '../lib/api';

type Stage =
  | { step: 'credentials' }
  | { step: 'totp'; challengeToken: string }
  | { step: 'enrol'; challengeToken: string; qrDataUrl: string; secret: string };

export function LoginPage() {
  const { login, verifyTotp } = useAuth();
  const navigate = useNavigate();

  const [stage, setStage] = useState<Stage>({ step: 'credentials' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submitCredentials(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await login(email, password);
      if (result.status === 'AUTHENTICATED') {
        navigate('/');
      } else if (result.status === 'TOTP_REQUIRED') {
        setStage({ step: 'totp', challengeToken: result.challengeToken });
      } else {
        setStage({
          step: 'enrol',
          challengeToken: result.challengeToken,
          qrDataUrl: result.qrDataUrl,
          secret: result.secret,
        });
      }
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not sign in');
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(event: React.FormEvent) {
    event.preventDefault();
    if (stage.step === 'credentials') return;
    setError(null);
    setBusy(true);
    try {
      await verifyTotp(stage.challengeToken, code);
      navigate('/');
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not verify the code');
      if (err instanceof ApiRequestError && err.code === 'CHALLENGE_EXPIRED') {
        setStage({ step: 'credentials' });
        setCode('');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        background: 'var(--ink-deep)',
        padding: 24,
      }}
    >
      <div
        style={{
          width: 'min(440px, 100%)',
          background: 'var(--white)',
          borderRadius: 24,
          padding: 32,
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          boxShadow: 'var(--shadow-modal)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
          <img src="/brand/logo-wordmark.png" alt="ESkoolHub" style={{ height: 34 }} />
          <div className="label">Super admin</div>
        </div>

        {stage.step === 'credentials' ? (
          <form onSubmit={submitCredentials} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="display" style={{ fontSize: 22 }}>
              Sign in
            </div>
            <label className="field">
              <span>Email</span>
              <input
                type="email"
                value={email}
                autoComplete="username"
                autoFocus
                required
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <label className="field">
              <span>Password</span>
              <input
                type="password"
                value={password}
                autoComplete="current-password"
                required
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            {error ? <div className="banner banner--error">{error}</div> : null}
            <button className="btn" type="submit" disabled={busy}>
              {busy ? 'Checking…' : 'Continue'}
            </button>
            <p className="caption" style={{ margin: 0 }}>
              Platform staff only. Every action on this dashboard is recorded in the audit log.
            </p>
          </form>
        ) : (
          <form onSubmit={submitCode} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="display" style={{ fontSize: 22 }}>
              {stage.step === 'enrol' ? 'Set up two-factor' : 'Two-factor code'}
            </div>

            {stage.step === 'enrol' ? (
              <>
                <p className="caption" style={{ margin: 0 }}>
                  Scan this with Google Authenticator, 1Password or Authy, then enter the six-digit code it
                  shows.
                </p>
                <img
                  src={stage.qrDataUrl}
                  alt="Two-factor QR code"
                  style={{ width: 200, height: 200, alignSelf: 'center', borderRadius: 12 }}
                />
                <div className="caption" style={{ textAlign: 'center', wordBreak: 'break-all' }}>
                  Can't scan? Enter this key: <span className="mono">{stage.secret}</span>
                </div>
              </>
            ) : (
              <p className="caption" style={{ margin: 0 }}>
                Enter the six-digit code from your authenticator app.
              </p>
            )}

            <label className="field">
              <span>Authentication code</span>
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                autoFocus
                required
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                style={{ fontFamily: 'var(--font-mono)', fontSize: 20, letterSpacing: '0.3em', textAlign: 'center' }}
              />
            </label>

            {error ? <div className="banner banner--error">{error}</div> : null}

            <button className="btn" type="submit" disabled={busy || code.length < 6}>
              {busy ? 'Verifying…' : stage.step === 'enrol' ? 'Confirm & sign in' : 'Sign in'}
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                setStage({ step: 'credentials' });
                setCode('');
                setError(null);
              }}
            >
              Back
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
